/**
 * Game mode arena, Round 2 (plan 47 «Round 2 — the contract» § 3): the state
 * of «No sé» (`reveal`) and «Ver solución» (`{ action: 'solution', turnIndex }`)
 * as the thread shows it. Pure: the hook (`useGameArena`) keeps the learner's
 * REQUESTS, `useVoiceSession().gameSolutions` brings the server's ANSWERS
 * (exactly one per request), and these selectors pair them.
 *
 * Pairing (Round 2 review, 2026-10-17): BY REQUEST ID. Each request carries
 * an id the server echoes in its `game.solution` (`requestId`). The first
 * version paired reveals by arrival order and «Ver solución» by a count of
 * answers per attempt; both broke on a reconnect — `gameSolutions` resets to
 * `[]` on every new connection, so answered cards fell back to «Buscando…»
 * and later took the NEXT connection's answers — and two reveals in flight
 * could swap answers. Now the answers are kept by id across connections
 * ({@link advanceSolutionAnswers}), and a request with no answer after
 * {@link SOLUTION_ANSWER_DEADLINE_MS} reads as failed (its answer was lost:
 * a reconnect, a dropped message): «No pudimos traer la solución», with
 * «Reintentar» on a «Ver solución».
 */
import type { TranscriptGameSolution } from '@orborix/shared-types';
import type { GameSolutionLive } from '@orborix/voice-client';
import type { TranscriptTurn } from '../../lib/sessionsApi.js';

/** What a solution request shows. */
export type GameSolutionView =
  | { status: 'loading' }
  | { status: 'ready'; solutions: ReadonlyArray<string> }
  /** The model call failed (or its answer never came): «No pudimos traer la solución». */
  | { status: 'failed' }
  /** Nothing to solve: the button hides, a reveal does not count as «vista». */
  | { status: 'refused' };

/**
 * How long a request waits for its answer before it reads as failed. The
 * server always answers (its model call is bounded at 8 s,
 * `GAME_SOLUTION_TIMEOUT_MS`), so past this the answer was lost on the way.
 */
export const SOLUTION_ANSWER_DEADLINE_MS = 20_000;

/** One «No sé» / «Ver solución» request as the server will answer it. */
export interface SolutionRequest {
  /** Sent as `requestId`, echoed in the answer. */
  id: string;
  /** When it was sent (ms epoch): the deadline counts from here. */
  sentAt: number;
}

/** The server's answers by request id, kept across reconnects. */
export type GameSolutionAnswers = ReadonlyMap<string, GameSolutionLive>;

export const NO_SOLUTION_ANSWERS: GameSolutionAnswers = new Map();

/**
 * Adds the answers of `now` (the hook's list) that are not known yet.
 * Returns `prev` itself when nothing is new — the list resets to `[]` on a
 * new connection, and the answers already known stay. An answer with no
 * `requestId` (a server older than the field) pairs with nothing.
 */
export function advanceSolutionAnswers(
  prev: GameSolutionAnswers,
  now: ReadonlyArray<GameSolutionLive>,
): GameSolutionAnswers {
  let next: Map<string, GameSolutionLive> | null = null;
  for (const a of now) {
    if (!a.requestId || prev.get(a.requestId) === a) continue;
    next ??= new Map(prev);
    next.set(a.requestId, a);
  }
  return next ?? prev;
}

function viewOf(answer: GameSolutionLive): GameSolutionView {
  if (answer.status === 'ready') return { status: 'ready', solutions: answer.solutions };
  return answer.reason === 'refused' ? { status: 'refused' } : { status: 'failed' };
}

/**
 * The state of one request at time `now`: its answer when it arrived; else
 * loading until {@link SOLUTION_ANSWER_DEADLINE_MS}, failed after.
 */
export function solutionRequestView(
  answers: GameSolutionAnswers,
  request: SolutionRequest,
  now: number,
): GameSolutionView {
  const answer = answers.get(request.id);
  if (answer) return viewOf(answer);
  return now - request.sentAt >= SOLUTION_ANSWER_DEADLINE_MS
    ? { status: 'failed' }
    : { status: 'loading' };
}

/** One «No sé» the learner pressed in a translate-it run. */
export interface RevealRequest extends SolutionRequest {
  /** The run it belongs to (its summary counts it as «vista»). */
  run: number;
  /** The last rendered message when it was asked: its card goes right after. */
  anchor: number;
  /** The coach bubble whose challenge was revealed. */
  challengeIndex: number;
  challenge: string;
  round: number;
}

/** A «No sé» card of the thread (live or history). */
export interface RevealCardModel {
  key: string;
  challenge: string;
  view: GameSolutionView;
}

/* -------------------------------------------------------------------------- */
/* History (`data.gameSolutions` of the transcript)                            */
/* -------------------------------------------------------------------------- */

/** The persisted rows as views: a row with no solution = the generation failed. */
function historyView(row: TranscriptGameSolution): GameSolutionView {
  return row.solutions.length > 0
    ? { status: 'ready', solutions: row.solutions }
    : { status: 'failed' };
}

/**
 * Attempt turn index → its «Ver solución» as the history shows it (the last
 * row asked for that attempt). Failed rows are left out: in the history there
 * is nothing to retry, and a failure says nothing about the attempt.
 */
export function historyAttemptSolutions(
  rows: ReadonlyArray<TranscriptGameSolution>,
): ReadonlyMap<number, GameSolutionView> {
  const out = new Map<number, GameSolutionView>();
  for (const r of rows) {
    if (r.source !== 'solution' || r.attemptTurnIndex === null || r.solutions.length === 0)
      continue;
    out.set(r.attemptTurnIndex, historyView(r));
  }
  return out;
}

/**
 * Turn index → the «No sé» cards that go after that turn in the session
 * detail (`afterTurnIndex`; `null` = before any turn → key `-1`).
 */
export function historyRevealsByTurn(
  rows: ReadonlyArray<TranscriptGameSolution>,
): ReadonlyMap<number, RevealCardModel[]> {
  const out = new Map<number, RevealCardModel[]>();
  rows.forEach((r, i) => {
    if (r.source !== 'reveal') return;
    const key = r.afterTurnIndex ?? -1;
    const list = out.get(key) ?? [];
    list.push({ key: `reveal-${i}`, challenge: r.challenge, view: historyView(r) });
    out.set(key, list);
  });
  return out;
}

/** The seeded history's solutions («Continuar»), placed on the seeded bubbles. */
export interface HistoryGameSolutions {
  /** Historical attempt turn index (`historicalTurn.turnIndex`) → its «Ver solución». */
  attempts: ReadonlyMap<number, GameSolutionView>;
  /** Seeded message index → the «No sé» cards that go right after it. */
  revealsAfter: ReadonlyMap<number, RevealCardModel[]>;
}

/**
 * Places the seeded history's solutions on the bubbles `transcriptToHistory`
 * builds from the same `turns` (it pushes the learner then the coach bubble
 * of each turn, skipping empty texts): a reveal after turn X goes after the
 * last bubble of the last turn ≤ X. `null` when there is nothing to show.
 */
export function placeHistoryGameSolutions(
  turns: ReadonlyArray<TranscriptTurn>,
  rows: ReadonlyArray<TranscriptGameSolution>,
): HistoryGameSolutions | null {
  if (rows.length === 0) return null;
  const lastMsgOfTurn: Array<{ turnIndex: number; msg: number }> = [];
  let count = 0;
  for (const t of turns) {
    if (t.userText && t.userText.trim().length > 0) count += 1;
    if (t.assistantText && t.assistantText.trim().length > 0) count += 1;
    if (count > 0) lastMsgOfTurn.push({ turnIndex: t.turnIndex, msg: count - 1 });
  }
  const revealsAfter = new Map<number, RevealCardModel[]>();
  for (const [after, cards] of historyRevealsByTurn(rows)) {
    let msg = -1;
    for (const e of lastMsgOfTurn) if (e.turnIndex <= after) msg = e.msg;
    if (msg === -1) continue;
    revealsAfter.set(msg, [...(revealsAfter.get(msg) ?? []), ...cards]);
  }
  return { attempts: historyAttemptSolutions(rows), revealsAfter };
}
